import { useEffect, useRef, useState } from 'react';
import { adsAdminApi } from '../../api/adsAdminApi';
import {
  formatCreativeStatus,
  formatPromotionStatus,
} from './travelManageConstants';
import { createRequestGuard, toSafeErrorMessage } from './adminPageUtils';

const ADS_REVIEW_EMPTY = '目前沒有可處理的廣告內容';

/** 預設可處理佇列的顯示篩選（資料來自 backend actionable set）。 */
const STATUS_FILTER_ALL = 'ALL';
const STATUS_FILTER_SUBMITTED = 'SUBMITTED';
const STATUS_FILTER_UNDER_REVIEW = 'UNDER_REVIEW';

function filterByStatus(items, statusFilter, statusKey = 'status') {
  if (!Array.isArray(items)) return [];
  if (statusFilter === STATUS_FILTER_ALL) return items;
  return items.filter((item) => String(item?.[statusKey] || '') === statusFilter);
}

function isConflictError(err) {
  if (!err) return false;
  if (err.response?.status === 409 || err.status === 409 || err.code === 95903) return true;
  const msg = String(err.message || '').toLowerCase();
  return msg.includes('conflict') || msg.includes('409') || msg.includes('版本');
}

function DetailRow({ label, value, className = '' }) {
  return (
    <div className="product-detail-row">
      <span className="product-detail-row__label">{label}</span>
      <span className={`product-detail-row__value ${className}`.trim()}>{value ?? '—'}</span>
    </div>
  );
}

export default function AdsReviewPage() {
  const [activeTab, setActiveTab] = useState('promotions'); // 'promotions' | 'creatives'
  const [statusFilter, setStatusFilter] = useState(STATUS_FILTER_ALL);
  const [promotions, setPromotions] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [conflictError, setConflictError] = useState(false);

  // Detail Modal
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [selectedCreative, setSelectedCreative] = useState(null);

  // Action Modals
  const [actionTarget, setActionTarget] = useState(null); // { type: 'promotion'|'creative', action: 'approve'|'reject', item: obj }
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [beginReviewingId, setBeginReviewingId] = useState(null);
  const requestGuard = useRef(createRequestGuard()).current;

  async function loadPromotions() {
    const token = requestGuard.next();
    setLoading(true);
    setError('');
    setConflictError(false);
    try {
      const data = await adsAdminApi.promotionsReviewQueue({ limit: 50 });
      if (!requestGuard.isCurrent(token)) return;
      setPromotions(Array.isArray(data) ? data : data?.records || []);
    } catch (err) {
      if (!requestGuard.isCurrent(token)) return;
      if (isConflictError(err)) {
        setConflictError(true);
      } else {
        setError(toSafeErrorMessage(err, '載入推廣審核隊列失敗，請稍後重試'));
      }
      setPromotions([]);
    } finally {
      if (requestGuard.isCurrent(token)) setLoading(false);
    }
  }

  async function loadCreatives() {
    const token = requestGuard.next();
    setLoading(true);
    setError('');
    setConflictError(false);
    try {
      const data = await adsAdminApi.creativesReviewQueue({ limit: 50 });
      if (!requestGuard.isCurrent(token)) return;
      setCreatives(Array.isArray(data) ? data : data?.records || []);
    } catch (err) {
      if (!requestGuard.isCurrent(token)) return;
      if (isConflictError(err)) {
        setConflictError(true);
      } else {
        setError(toSafeErrorMessage(err, '載入素材審核隊列失敗，請稍後重試'));
      }
      setCreatives([]);
    } finally {
      if (requestGuard.isCurrent(token)) setLoading(false);
    }
  }

  function handleRefresh() {
    setError('');
    setMessage('');
    setConflictError(false);
    if (activeTab === 'promotions') {
      loadPromotions();
    } else {
      loadCreatives();
    }
  }

  useEffect(() => {
    handleRefresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Begin Review Handlers
  async function handleBeginPromotionReview(item) {
    const id = String(item.promotionApplicationId);
    if (beginReviewingId) return;
    setBeginReviewingId(id);
    setError('');
    setMessage('');
    setConflictError(false);
    try {
      const res = await adsAdminApi.beginPromotionReview(id, {
        expectedRowVersion: item.rowVersion,
      });
      setMessage(`推廣申請 #${id} 已進入審核中`);
      // 立即以回傳結果更新列，避免 reload 前短暫消失；backend 現已回傳 UNDER_REVIEW
      setPromotions((prev) =>
        prev.map((p) => (String(p.promotionApplicationId) === id ? { ...p, ...res } : p)),
      );
      if (String(selectedPromotion?.promotionApplicationId) === id) {
        setSelectedPromotion(res);
      }
      loadPromotions();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictError(true);
      } else {
        setError(toSafeErrorMessage(err, '開始審核失敗，請稍後重試'));
      }
    } finally {
      setBeginReviewingId(null);
    }
  }

  async function handleBeginCreativeReview(item) {
    const id = String(item.creativeId);
    if (beginReviewingId) return;
    setBeginReviewingId(id);
    setError('');
    setMessage('');
    setConflictError(false);
    try {
      const res = await adsAdminApi.beginCreativeReview(id, {
        expectedRowVersion: item.rowVersion,
      });
      setMessage(`素材 #${id} 已進入審核中`);
      setCreatives((prev) =>
        prev.map((c) => (String(c.creativeId) === id ? { ...c, ...res } : c)),
      );
      if (String(selectedCreative?.creativeId) === id) {
        setSelectedCreative(res);
      }
      loadCreatives();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictError(true);
      } else {
        setError(toSafeErrorMessage(err, '開始審核失敗，請稍後重試'));
      }
    } finally {
      setBeginReviewingId(null);
    }
  }

  // Open Action Dialogs
  function openApproveModal(type, item) {
    setActionTarget({ type, action: 'approve', item });
    setError('');
    setMessage('');
  }

  function openRejectModal(type, item) {
    setActionTarget({ type, action: 'reject', item });
    setRejectionReason('');
    setRejectionError('');
    setError('');
    setMessage('');
  }

  function closeActionModal() {
    setActionTarget(null);
    setRejectionReason('');
    setRejectionError('');
    setSubmitting(false);
  }

  // Submit Approve / Reject
  async function handleConfirmAction() {
    if (!actionTarget || submitting) return;
    const { type, action, item } = actionTarget;

    if (action === 'reject') {
      const trimmed = rejectionReason.trim();
      if (!trimmed) {
        setRejectionError('請填寫駁回原因');
        return;
      }
      if (trimmed.length > 500) {
        setRejectionError('駁回原因不可超過 500 字');
        return;
      }
    }

    setSubmitting(true);
    setError('');
    setConflictError(false);
    try {
      if (type === 'promotion') {
        const id = String(item.promotionApplicationId);
        if (action === 'approve') {
          await adsAdminApi.approvePromotion(id, { expectedRowVersion: item.rowVersion });
          setMessage(`推廣申請 #${id} 審核通過`);
        } else {
          await adsAdminApi.rejectPromotion(id, {
            expectedRowVersion: item.rowVersion,
            rejectionReason: rejectionReason.trim(),
          });
          setMessage(`推廣申請 #${id} 已駁回`);
        }
        // terminal 立即自本地 actionable 列表移除，再 reload 對齊
        setPromotions((prev) => prev.filter((p) => String(p.promotionApplicationId) !== id));
        loadPromotions();
        setSelectedPromotion(null);
      } else {
        const id = String(item.creativeId);
        if (action === 'approve') {
          await adsAdminApi.approveCreative(id, { expectedRowVersion: item.rowVersion });
          setMessage(`廣告素材 #${id} 審核通過`);
        } else {
          await adsAdminApi.rejectCreative(id, {
            expectedRowVersion: item.rowVersion,
            rejectionReason: rejectionReason.trim(),
          });
          setMessage(`廣告素材 #${id} 已駁回`);
        }
        setCreatives((prev) => prev.filter((c) => String(c.creativeId) !== id));
        loadCreatives();
        setSelectedCreative(null);
      }
      closeActionModal();
    } catch (err) {
      if (isConflictError(err)) {
        setConflictError(true);
        closeActionModal();
      } else {
        setError(toSafeErrorMessage(err, '操作失敗，請稍後重試'));
        setSubmitting(false);
      }
    }
  }

  const visiblePromotions = filterByStatus(promotions, statusFilter);
  const visibleCreatives = filterByStatus(creatives, statusFilter);

  return (
    <div className="page-card" data-testid="ads-review-page">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>廣告審核</h2>
        <button type="button" className="btn btn-secondary" onClick={handleRefresh} disabled={loading} data-testid="ads-review-refresh">
          重新整理
        </button>
      </div>

      {/* Tabs */}
      <div className="tab-bar" style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        <button
          type="button"
          className={`btn ${activeTab === 'promotions' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('promotions')}
          data-testid="tab-promotions"
        >
          推廣申請審核
        </button>
        <button
          type="button"
          className={`btn ${activeTab === 'creatives' ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setActiveTab('creatives')}
          data-testid="tab-creatives"
        >
          廣告素材審核
        </button>
      </div>

      {/* 可處理狀態顯示篩選：全部＝SUBMITTED＋UNDER_REVIEW */}
      <div
        className="tab-bar"
        role="group"
        aria-label="審核狀態篩選"
        style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}
        data-testid="ads-review-status-filter"
      >
        <button
          type="button"
          className={`btn btn-sm ${statusFilter === STATUS_FILTER_ALL ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setStatusFilter(STATUS_FILTER_ALL)}
          data-testid="filter-status-all"
        >
          全部待處理
        </button>
        <button
          type="button"
          className={`btn btn-sm ${statusFilter === STATUS_FILTER_SUBMITTED ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setStatusFilter(STATUS_FILTER_SUBMITTED)}
          data-testid="filter-status-submitted"
        >
          待審核
        </button>
        <button
          type="button"
          className={`btn btn-sm ${statusFilter === STATUS_FILTER_UNDER_REVIEW ? 'btn-primary' : 'btn-secondary'}`}
          onClick={() => setStatusFilter(STATUS_FILTER_UNDER_REVIEW)}
          data-testid="filter-status-under-review"
        >
          審核中
        </button>
      </div>

      {/* Conflict Banner */}
      {conflictError ? (
        <div
          className="alert alert-warning alert-conflict"
          role="alert"
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}
          data-testid="conflict-alert"
        >
          <span>⚠️ 資料已被更新或有其他審核人操作中（版本衝突），請重新整理以取得最新資料。</span>
          <button type="button" className="btn btn-secondary btn-sm" onClick={handleRefresh}>
            立即重新整理
          </button>
        </div>
      ) : null}

      {/* Error & Message */}
      {error ? (
        <div className="alert alert-danger" role="alert" style={{ marginBottom: '16px' }} data-testid="ads-review-error">
          {error}
          <button type="button" className="btn btn-secondary btn-sm" style={{ marginLeft: 8 }} onClick={handleRefresh} data-testid="ads-review-retry">
            重試
          </button>
        </div>
      ) : null}
      {message ? (
        <div className="alert alert-success" role="alert" style={{ marginBottom: '16px' }} data-testid="ads-review-success">
          {message}
        </div>
      ) : null}

      {/* Promotions Table */}
      {activeTab === 'promotions' ? (
        <div className="table-responsive">
          <table className="admin-table" data-testid="promotions-table">
            <thead>
              <tr>
                <th>申請編號</th>
                <th>申請會員 ID</th>
                <th>旅程標的 ID</th>
                <th>標的類型</th>
                <th>審核狀態</th>
                <th>版本號</th>
                <th>提交時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>載入中…</td>
                </tr>
              ) : visiblePromotions.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }} data-testid="ads-review-empty">{ADS_REVIEW_EMPTY}</td>
                </tr>
              ) : (
                visiblePromotions.map((p) => (
                  <tr key={String(p.promotionApplicationId)} data-testid={`promotion-row-${p.promotionApplicationId}`}>
                    <td data-testid="promotion-id">{String(p.promotionApplicationId)}</td>
                    <td>{String(p.applicantMemberId ?? '—')}</td>
                    <td data-testid="source-content-id">{String(p.sourceContentId ?? '—')}</td>
                    <td>{p.sourceContentType || '—'}</td>
                    <td>
                      <span className={`badge badge-${p.status === 'APPROVED' ? 'success' : p.status === 'UNDER_REVIEW' ? 'info' : p.status === 'REJECTED' ? 'danger' : 'warning'}`}>
                        {formatPromotionStatus(p.status)}
                      </span>
                    </td>
                    <td>{p.rowVersion}</td>
                    <td>{p.submittedAt ? String(p.submittedAt).replace('T', ' ').slice(0, 19) : '—'}</td>
                    <td style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelectedPromotion(p)}
                        data-testid="view-promotion-detail"
                      >
                        查看
                      </button>
                      {p.status === 'SUBMITTED' ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleBeginPromotionReview(p)}
                          disabled={Boolean(beginReviewingId)}
                          data-testid="begin-promotion-review-btn"
                        >
                          {beginReviewingId === String(p.promotionApplicationId) ? '處理中…' : '開始審核'}
                        </button>
                      ) : null}
                      {p.status === 'UNDER_REVIEW' ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => openApproveModal('promotion', p)}
                            data-testid="approve-promotion-btn"
                          >
                            通過
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => openRejectModal('promotion', p)}
                            data-testid="reject-promotion-btn"
                          >
                            駁回
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Creatives Table */}
      {activeTab === 'creatives' ? (
        <div className="table-responsive">
          <table className="admin-table" data-testid="creatives-table">
            <thead>
              <tr>
                <th>素材編號</th>
                <th>推廣申請 ID</th>
                <th>標題文案</th>
                <th>導向類型</th>
                <th>審核狀態</th>
                <th>版本號</th>
                <th>建立時間</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }}>載入中…</td>
                </tr>
              ) : visibleCreatives.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center' }} data-testid="ads-review-empty">{ADS_REVIEW_EMPTY}</td>
                </tr>
              ) : (
                visibleCreatives.map((c) => (
                  <tr key={String(c.creativeId)} data-testid={`creative-row-${c.creativeId}`}>
                    <td data-testid="creative-id">{String(c.creativeId)}</td>
                    <td>{String(c.promotionApplicationId ?? '—')}</td>
                    <td>{c.headline || '—'}</td>
                    <td>{c.destinationType || '—'}</td>
                    <td>
                      <span className={`badge badge-${c.status === 'APPROVED' ? 'success' : c.status === 'UNDER_REVIEW' ? 'info' : c.status === 'REJECTED' ? 'danger' : 'warning'}`}>
                        {formatCreativeStatus(c.status)}
                      </span>
                    </td>
                    <td>{c.rowVersion}</td>
                    <td>{c.createTime ? String(c.createTime).replace('T', ' ').slice(0, 19) : '—'}</td>
                    <td style={{ display: 'flex', gap: '6px' }}>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => setSelectedCreative(c)}
                        data-testid="view-creative-detail"
                      >
                        查看
                      </button>
                      {c.status === 'SUBMITTED' ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-sm"
                          onClick={() => handleBeginCreativeReview(c)}
                          disabled={Boolean(beginReviewingId)}
                          data-testid="begin-creative-review-btn"
                        >
                          {beginReviewingId === String(c.creativeId) ? '處理中…' : '開始審核'}
                        </button>
                      ) : null}
                      {c.status === 'UNDER_REVIEW' ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-success btn-sm"
                            onClick={() => openApproveModal('creative', c)}
                            data-testid="approve-creative-btn"
                          >
                            通過
                          </button>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => openRejectModal('creative', c)}
                            data-testid="reject-creative-btn"
                          >
                            駁回
                          </button>
                        </>
                      ) : null}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : null}

      {/* Promotion Detail Modal */}
      {selectedPromotion ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => setSelectedPromotion(null)}>
          <div
            className="admin-modal admin-modal--wide"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="promotion-detail-modal"
          >
            <div className="admin-modal__header">
              <h3>推廣申請詳情 #{String(selectedPromotion.promotionApplicationId)}</h3>
              <button type="button" className="admin-modal__close" onClick={() => setSelectedPromotion(null)}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <div className="product-detail-grid">
                <DetailRow label="申請編號" value={String(selectedPromotion.promotionApplicationId)} />
                <DetailRow label="申請會員 ID" value={String(selectedPromotion.applicantMemberId ?? '—')} />
                <DetailRow label="內容標的 ID" value={String(selectedPromotion.sourceContentId ?? '—')} />
                <DetailRow label="標的類型" value={selectedPromotion.sourceContentType} />
                <DetailRow label="標的擁有者 ID" value={String(selectedPromotion.sourceOwnerMemberId ?? '—')} />
                <DetailRow label="標的創作者 ID" value={String(selectedPromotion.sourceCreatorMemberId ?? '—')} />
                <DetailRow label="審核狀態" value={formatPromotionStatus(selectedPromotion.status)} />
                <DetailRow label="資料版本 (CAS)" value={selectedPromotion.rowVersion} />
                <DetailRow label="標的推廣資格" value={selectedPromotion.sourceEligible ? '合規' : '不合規 / 待確認'} />
                <DetailRow label="提交時間" value={selectedPromotion.submittedAt ? String(selectedPromotion.submittedAt).replace('T', ' ').slice(0, 19) : '—'} />
                <DetailRow label="審核時間" value={selectedPromotion.reviewedAt ? String(selectedPromotion.reviewedAt).replace('T', ' ').slice(0, 19) : '—'} />
                {selectedPromotion.rejectionReason ? (
                  <DetailRow label="駁回原因" value={selectedPromotion.rejectionReason} className="text-danger" />
                ) : null}
              </div>
            </div>
            <div className="admin-modal__footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {selectedPromotion.status === 'SUBMITTED' ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleBeginPromotionReview(selectedPromotion)}
                  >
                    開始審核
                  </button>
                ) : null}
                {selectedPromotion.status === 'UNDER_REVIEW' ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => openApproveModal('promotion', selectedPromotion)}
                    >
                      通過審核
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => openRejectModal('promotion', selectedPromotion)}
                    >
                      駁回審核
                    </button>
                  </div>
                ) : null}
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedPromotion(null)}>
                關閉
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Creative Detail Modal */}
      {selectedCreative ? (
        <div className="admin-modal-overlay" role="presentation" onClick={() => setSelectedCreative(null)}>
          <div
            className="admin-modal admin-modal--wide"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="creative-detail-modal"
          >
            <div className="admin-modal__header">
              <h3>素材詳情 #{String(selectedCreative.creativeId)}</h3>
              <button type="button" className="admin-modal__close" onClick={() => setSelectedCreative(null)}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <div className="product-detail-grid">
                <DetailRow label="素材編號" value={String(selectedCreative.creativeId)} />
                <DetailRow label="推廣申請 ID" value={String(selectedCreative.promotionApplicationId ?? '—')} />
                <DetailRow label="廣告標題" value={selectedCreative.headline || '—'} />
                <DetailRow label="廣告摘要" value={selectedCreative.summary || '—'} />
                <DetailRow label="導向類型" value={selectedCreative.destinationType || '—'} />
                <DetailRow label="導向內容 ID" value={String(selectedCreative.destinationContentId ?? '—')} />
                <DetailRow label="審核狀態" value={formatCreativeStatus(selectedCreative.status)} />
                <DetailRow label="資料版本 (CAS)" value={selectedCreative.rowVersion} />
                <DetailRow label="建立時間" value={selectedCreative.createTime ? String(selectedCreative.createTime).replace('T', ' ').slice(0, 19) : '—'} />
                {selectedCreative.coverFileReference ? (
                  <div className="product-detail-row" style={{ gridColumn: 'span 2' }}>
                    <span className="product-detail-row__label">封面圖片</span>
                    <div style={{ marginTop: '8px' }}>
                      <img
                        src={selectedCreative.coverFileReference}
                        alt="素材封面"
                        style={{ maxWidth: '280px', maxHeight: '180px', objectFit: 'cover', borderRadius: '4px' }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>
                        {selectedCreative.coverFileReference}
                      </div>
                    </div>
                  </div>
                ) : null}
                {selectedCreative.rejectionReason ? (
                  <DetailRow label="駁回原因" value={selectedCreative.rejectionReason} className="text-danger" />
                ) : null}
              </div>
            </div>
            <div className="admin-modal__footer" style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {selectedCreative.status === 'SUBMITTED' ? (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => handleBeginCreativeReview(selectedCreative)}
                  >
                    開始審核
                  </button>
                ) : null}
                {selectedCreative.status === 'UNDER_REVIEW' ? (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      className="btn btn-success"
                      onClick={() => openApproveModal('creative', selectedCreative)}
                    >
                      通過審核
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={() => openRejectModal('creative', selectedCreative)}
                    >
                      駁回審核
                    </button>
                  </div>
                ) : null}
              </div>
              <button type="button" className="btn btn-secondary" onClick={() => setSelectedCreative(null)}>
                關閉
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Approve Confirmation Modal */}
      {actionTarget && actionTarget.action === 'approve' ? (
        <div className="admin-modal-overlay" role="presentation" onClick={closeActionModal}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="approve-modal"
          >
            <div className="admin-modal__header">
              <h3>確認通過審核</h3>
              <button type="button" className="admin-modal__close" onClick={closeActionModal}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <p>
                您確定要核准此{actionTarget.type === 'promotion' ? '推廣申請' : '廣告素材'}？
              </p>
              <p style={{ color: '#666', fontSize: '13px' }}>
                項目編號：#{String(actionTarget.type === 'promotion' ? actionTarget.item.promotionApplicationId : actionTarget.item.creativeId)}<br />
                當前版本號：{actionTarget.item.rowVersion}
              </p>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={closeActionModal} disabled={submitting}>
                取消
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={handleConfirmAction}
                disabled={submitting}
                data-testid="confirm-approve-btn"
              >
                {submitting ? '提交中…' : '確認通過'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Reject Modal */}
      {actionTarget && actionTarget.action === 'reject' ? (
        <div className="admin-modal-overlay" role="presentation" onClick={closeActionModal}>
          <div
            className="admin-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            data-testid="reject-modal"
          >
            <div className="admin-modal__header">
              <h3>駁回審核</h3>
              <button type="button" className="admin-modal__close" onClick={closeActionModal}>
                ×
              </button>
            </div>
            <div className="admin-modal__body">
              <p>
                項目編號：#{String(actionTarget.type === 'promotion' ? actionTarget.item.promotionApplicationId : actionTarget.item.creativeId)}
              </p>
              <div className="form-field" style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label htmlFor="rejectionReasonInput">
                  駁回理由 <span style={{ color: 'red' }}>*</span>
                </label>
                <textarea
                  id="rejectionReasonInput"
                  className="form-control"
                  rows="4"
                  placeholder="請輸入駁回原因（必填，上限 500 字）"
                  value={rejectionReason}
                  onChange={(e) => {
                    setRejectionReason(e.target.value);
                    if (rejectionError) setRejectionError('');
                  }}
                  data-testid="rejection-reason-input"
                />
                {rejectionError ? (
                  <span className="text-danger" style={{ fontSize: '12px', color: '#dc3545' }} data-testid="rejection-error">
                    {rejectionError}
                  </span>
                ) : null}
              </div>
            </div>
            <div className="admin-modal__footer">
              <button type="button" className="btn btn-secondary" onClick={closeActionModal} disabled={submitting}>
                取消
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmAction}
                disabled={submitting || !rejectionReason.trim()}
                data-testid="confirm-reject-btn"
              >
                {submitting ? '提交中…' : '確認駁回'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
